import type { UIMessage } from "ai";

export type ChatStreamingState = "idle" | "streaming" | "interrupted";

export type ChatRetryAction = "retry" | "recover" | "reconcile" | "interrupted";

interface SendableChat {
    error?: Error;
    sendMessage(message: UIMessage): Promise<void>;
}

interface RetryableChat {
    sendMessage(message: { parts: UIMessage["parts"]; metadata?: UIMessage["metadata"]; messageId: string }): Promise<void>;
}

export async function sendChatMessage(chat: SendableChat, message: UIMessage): Promise<void> {
    await chat.sendMessage(message);
    if (chat.error) throw chat.error;
}

export async function resendChatMessage(chat: RetryableChat, message: UIMessage): Promise<void> {
    await chat.sendMessage({
        parts: message.parts,
        metadata: message.metadata,
        messageId: message.id,
    });
}

export interface ChatRetryStatus {
    streamingState: ChatStreamingState;
    messages?: Array<{ id: string; role: string }>;
}

export function chatRetryAction(status: ChatRetryStatus, retryMessageId: string): ChatRetryAction {
    if (status.streamingState === "interrupted") return "interrupted";
    if (status.streamingState === "streaming") return "recover";

    const retryMessageIndex = status.messages?.findIndex((message) => message.id === retryMessageId) ?? -1;
    const hasAssistantResponse = retryMessageIndex >= 0 && status.messages?.slice(retryMessageIndex + 1).some((message) => message.role === "assistant");
    return hasAssistantResponse ? "reconcile" : "retry";
}
